import { subunitFactor } from "./locale-data";

const formatters = new Map<string, Intl.NumberFormat>();

let displayCurrencyConfig: string | null = null;
let displayLocaleConfig = "en-US";
let exchangeRatesConfig: Record<string, number> = {};

/** Configure the global display currency, exchange rates, and locale.
 *  Called once from the app layout when shared props change. */
export function configureDisplayCurrency(currency: string, rates: Record<string, number>, locale?: string): void {
  displayCurrencyConfig = currency;
  exchangeRatesConfig = rates;
  if (locale && locale !== displayLocaleConfig) {
    displayLocaleConfig = locale;
    formatters.clear();
  }
}

/** Returns the currently configured display locale (e.g. "de", "en-GB"). */
export function getDisplayLocale(): string {
  return displayLocaleConfig;
}

type FormatPrecision = "subunit" | "whole";

function getFormatter(currency: string, precision: FormatPrecision = "subunit"): Intl.NumberFormat {
  const upper = currency.toUpperCase();
  const key = `${displayLocaleConfig}-${upper}-${precision}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    const options: Intl.NumberFormatOptions = { style: "currency", currency: upper };
    if (precision === "whole") options.maximumFractionDigits = 0;
    formatter = new Intl.NumberFormat(displayLocaleConfig, options);
    formatters.set(key, formatter);
  }
  return formatter;
}

/** Returns the rate for a currency relative to USD (the base). USD itself returns 1.
 *  Returns null when no rate is available for a non-USD currency. */
function rateForCurrency(currency: string): number | null {
  if (currency.toUpperCase() === "USD") return 1;
  return exchangeRatesConfig[currency.toUpperCase()] ?? null;
}

/** Returns the currently configured display currency code (e.g. "GBP"), or null if not set. */
export function getDisplayCurrency(): string | null {
  return displayCurrencyConfig;
}

/** Returns the exchange rate from `sourceCurrency` to the display currency.
 *  Returns 1 when no conversion is needed (same currency or no display currency configured).
 *  Returns null only when rates are unavailable for a required conversion. */
export function getDisplayExchangeRate(sourceCurrency: string): number | null {
  if (!displayCurrencyConfig || displayCurrencyConfig === sourceCurrency) return 1;
  const sourceRate = rateForCurrency(sourceCurrency);
  const displayRate = rateForCurrency(displayCurrencyConfig);
  if (sourceRate === null || displayRate === null) return null;
  return displayRate / sourceRate;
}

/** Returns the symbol for a currency under the display locale (e.g. "$", "£", "€"). */
export function getCurrencySymbol(currency: string): string {
  const parts = getFormatter(currency).formatToParts(0);
  return parts.find((part) => part.type === "currency")?.value ?? currency;
}

/** Returns the currency symbol for the display currency (e.g. "$", "£", "€"). */
export function getDisplayCurrencySymbol(): string {
  return getCurrencySymbol(displayCurrencyConfig ?? "USD");
}

function convertAndFormat(cents: number, currency: string, precision: FormatPrecision): string {
  const amount = cents / subunitFactor(currency);

  if (displayCurrencyConfig && displayCurrencyConfig !== currency) {
    const rate = getDisplayExchangeRate(currency);
    if (rate !== null) {
      return getFormatter(displayCurrencyConfig, precision).format(amount * rate);
    }
  }
  return getFormatter(currency, precision).format(amount);
}

export function formatPrice(cents: number, currency: string): string {
  return convertAndFormat(cents, currency, "subunit");
}

/** Like formatPrice but rounded to whole units. Use on dense surfaces (card pills, list rows)
 *  where the cents/pence add visual noise without informational value. */
export function formatPriceWhole(cents: number, currency: string): string {
  return convertAndFormat(cents, currency, "whole");
}

/** Returns true when the display currency is configured and differs from the source currency. */
export function isDisplayConverted(sourceCurrency: string): boolean {
  return displayCurrencyConfig !== null && displayCurrencyConfig !== sourceCurrency;
}

/** Formats a price in its native (source) currency, ignoring display currency conversion. */
export function formatNativePrice(cents: number, currency: string): string {
  const amount = cents / subunitFactor(currency);
  return getFormatter(currency).format(amount);
}
